/**
 * Org Structure Tab — a LIVE projection of Role Master.
 *
 * The taxonomy (Functions / Departments / Teams) is derived on read from
 * RoleMasterEntry.functional_area / department / team_1-3 — there is no separate
 * OrganizationalUnit copy, so it cannot drift. "Renaming" a node bulk-updates every
 * active Role Master entry carrying that value at that level (RM is the source of
 * truth). Functions also show their F&O JobFamily sync status; Reconcile / Push
 * Families keep Role Master and F&O aligned.
 *
 * Sub-tabs:
 *   1. Functions          — distinct RoleMasterEntry.functional_area + F&O sync status
 *   2. Departments & Teams — department -> team_1 -> team_2 -> team_3 hierarchy
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  RefreshCw, Pencil, Loader2, Building2, FolderTree, ChevronRight, ChevronDown,
  X, CheckCircle, AlertTriangle, GitCompareArrows, Upload, ArrowRight, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useConfirm } from '@/hooks/useConfirm';
import organizationService, {
  jobFamilySyncService,
  OrgStructureNode,
  OrgUnitTree,
} from '@/services/api/organizationService';
import ReassignJobsDialog from './ReassignJobsDialog';
import FunctionReconcileDialog from './FunctionReconcileDialog';

const OrgStructureTab: React.FC = () => {
  const { confirm: showConfirm } = useConfirm();
  const [treeData, setTreeData] = useState<OrgUnitTree | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [hideEmptyFunctions, setHideEmptyFunctions] = useState(false);
  const [reassignFn, setReassignFn] = useState<OrgStructureNode | null>(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    loadTree();
  }, []);

  const loadTree = async (refresh = false) => {
    setIsLoading(true);
    try {
      setTreeData(await organizationService.getOrgUnitTree(refresh));
    } catch (error) {
      console.error('Failed to load org tree:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const startRename = (node: OrgStructureNode) => { setEditingId(node.id); setEditingName(node.name); };

  const saveRename = async () => {
    if (!editingId || !editingName.trim()) return;
    setSavingRename(true);
    try {
      const res = await organizationService.renameOrgTaxonomy({ id: editingId, new_name: editingName.trim() });
      setSyncMessage(
        res.updated > 0
          ? `Renamed across ${res.updated} role${res.updated === 1 ? '' : 's'}.`
          : (res.message || 'No change.'),
      );
      setEditingId(null);
      await loadTree(true);
    } catch (error: any) {
      setSyncMessage(error?.response?.data?.detail || 'Rename failed');
    } finally {
      setSavingRename(false);
    }
  };

  // Push Role functions -> F&O Jobs whose family is empty (RM -> F&O).
  const handlePushFamilies = async () => {
    setSyncMessage('Checking linked roles with empty Job families...');
    try {
      const preview = await jobFamilySyncService.pushRoleFamiliesToJobs({ dry_run: true });
      if (!preview.would_update || preview.would_update === 0) {
        setSyncMessage('All linked Jobs already have their family set. Nothing to push.');
        return;
      }
      const confirmed = await showConfirm(
        `Found ${preview.would_update} linked Jobs (of ${preview.total_linked} total) where the Job's family is empty but the Role has a function set.\n\nPush Role function → Job family for all? This also cascades to PositionDetails.`,
        'Push Role Families to Jobs',
        { confirmText: `Push ${preview.would_update}`, type: 'warning' },
      );
      if (!confirmed) { setSyncMessage(null); return; }
      setSyncMessage(`Pushing families to ${preview.would_update} Jobs...`);
      const result = await jobFamilySyncService.pushRoleFamiliesToJobs({ dry_run: false });
      setSyncMessage(`Pushed ${result.succeeded} Job families${result.failed ? `, ${result.failed} failed` : ''}`);
      loadTree(true);
    } catch (e) { console.error(e); setSyncMessage('Push failed'); }
  };

  // Align F&O PositionDetail / JobDetail families to this Function's F&O JobFamilyId.
  const handleFixFamilies = async (node: OrgStructureNode) => {
    const fam = node.fo_job_family_id;
    if (!fam) return;
    setSyncMessage(`Checking families for "${node.name}" Jobs...`);
    try {
      const preview = await jobFamilySyncService.fixPositionJobFamilies({ for_job_family: fam, dry_run: true });
      if (preview.would_fix === 0) {
        setSyncMessage(`All Position & Job families for "${node.name}" are correct.`);
        return;
      }
      const posCount = (preview as any).position_details ?? preview.would_fix;
      const jobCount = (preview as any).job_details ?? 0;
      const parts: string[] = [];
      if (posCount > 0) parts.push(`${posCount} PositionDetail`);
      if (jobCount > 0) parts.push(`${jobCount} JobDetail`);
      const confirmed = await showConfirm(
        `Found ${preview.would_fix} records (${parts.join(' + ')}) for Jobs in "${node.name}" where the family doesn't match.\n\nFix all by aligning to "${fam}"?`,
        `Fix "${node.name}" Families`,
        { confirmText: `Fix ${preview.would_fix}`, type: 'warning' },
      );
      if (!confirmed) { setSyncMessage(null); return; }
      setSyncMessage(`Fixing ${preview.would_fix} records for "${node.name}"...`);
      const result = await jobFamilySyncService.fixPositionJobFamilies({ for_job_family: fam, dry_run: false });
      const rp: string[] = [];
      if ((result as any).positions_fixed) rp.push(`${(result as any).positions_fixed} positions`);
      if ((result as any).jobs_fixed) rp.push(`${(result as any).jobs_fixed} jobs`);
      setSyncMessage(`${node.name}: fixed ${rp.join(' + ') || result.succeeded}${result.failed ? `, ${result.failed} failed` : ''}`);
    } catch (e) { console.error(e); setSyncMessage('Fix failed'); }
  };

  // Create a Role Master Function in F&O as a JobFamily (RM -> F&O).
  // Only offered for "Local only" functions — a synced one is already there.
  const handlePushFunction = async (fn: OrgStructureNode) => {
    setSyncMessage(`Checking "${fn.name}" against F&O...`);
    try {
      // Preview first so the confirm shows the exact JobFamilyId that will be written,
      // then send that same id back so what was approved is what lands.
      const preview = await jobFamilySyncService.pushToFO({ functionName: fn.name, dryRun: true });
      const confirmed = await showConfirm(
        `Create Job Family "${preview.job_family_id}" in F&O, described as "${preview.description}"?\n\n` +
        `This only makes the Function exist in F&O so Jobs can be assigned to it. ` +
        `No Jobs or Positions are moved.`,
        'Push Function to F&O',
        { confirmText: 'Push to F&O', type: 'warning' },
      );
      if (!confirmed) { setSyncMessage(null); return; }
      setSyncMessage(`Pushing "${fn.name}" to F&O...`);
      const result = await jobFamilySyncService.pushToFO({
        functionName: fn.name,
        jobFamilyId: preview.job_family_id,
      });
      setSyncMessage(result.message);
      await loadTree(true);
    } catch (error: any) {
      console.error(error);
      setSyncMessage(error?.response?.data?.detail || `Failed to push "${fn.name}" to F&O`);
    }
  };

  const renameInput = () => (
    <span className="flex items-center gap-1 flex-1">
      <Input value={editingName} onChange={e => setEditingName(e.target.value)} autoFocus
        className="h-7 text-sm"
        onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditingId(null); }} />
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={saveRename} disabled={savingRename}>
        {savingRename ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />}
      </Button>
      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditingId(null)}><X className="h-3.5 w-3.5" /></Button>
    </span>
  );

  const rolesBadge = (n: number) => (
    <Badge variant="outline" className="text-xs">{n} role{n === 1 ? '' : 's'}</Badge>
  );

  // Recursive Department -> Team renderer.
  const renderTreeNode = (node: OrgStructureNode, depth = 0): React.ReactNode => {
    const hasChildren = node.children && node.children.length > 0;
    const isOpen = expanded.has(node.id);
    const isEditing = editingId === node.id;
    return (
      <div key={node.id}>
        <div className="group flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded" style={{ paddingLeft: depth * 22 + 8 }}>
          {hasChildren ? (
            <button onClick={() => toggle(node.id)} className="p-0.5">
              {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
            </button>
          ) : <span className="w-5" />}
          {isEditing ? renameInput() : (
            <>
              <span className="text-sm flex-1">{node.name}</span>
              {rolesBadge(node.roles_count)}
              {node.unit_type === 'department' && (node.teams_count ?? 0) > 0 && (
                <Badge variant="outline" className="text-xs">{node.teams_count} team{node.teams_count === 1 ? '' : 's'}</Badge>
              )}
              <Button size="sm" variant="ghost" onClick={() => startRename(node)}
                className="h-7 px-1.5 opacity-0 group-hover:opacity-100" title="Rename across Role Master">
                <Pencil className="h-3 w-3" />
              </Button>
            </>
          )}
        </div>
        {isOpen && hasChildren && node.children.map(c => renderTreeNode(c, depth + 1))}
      </div>
    );
  };

  const renderFunctionRow = (fn: OrgStructureNode) => {
    const isEditing = editingId === fn.id;
    const synced = fn.fo_status === 'synced';
    return (
      <div key={fn.id} className="group flex items-center gap-2 py-1.5 px-2 hover:bg-gray-50 rounded">
        <span className="w-5" />
        {isEditing ? renameInput() : (
          <>
            <span className="text-sm flex-1">{fn.name}</span>
            <span className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${synced ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
              title={synced ? 'Matches an F&O Job Family' : 'No matching F&O Job Family'}>
              {synced ? 'F&O' : 'Local only'}
            </span>
            {rolesBadge(fn.roles_count)}
            {(fn.fo_jobs_count ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs">
                {hideEmptyFunctions ? (fn.fo_active_jobs_count ?? fn.fo_jobs_count) : fn.fo_jobs_count} jobs
              </Badge>
            )}
            {(fn.fo_positions_count ?? 0) > 0 && (
              <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200">{fn.fo_positions_count} pos</Badge>
            )}
            {synced && (fn.fo_jobs_count ?? 0) > 0 && (
              <Button size="sm" variant="ghost" onClick={() => setReassignFn(fn)}
                className="h-7 px-1.5 opacity-0 group-hover:opacity-100 text-indigo-600" title="Reassign F&O Jobs from this Function">
                <ArrowRight className="h-3 w-3" />
              </Button>
            )}
            {synced && (
              <Button size="sm" variant="ghost" onClick={() => handleFixFamilies(fn)}
                className="h-7 px-1.5 opacity-0 group-hover:opacity-100 text-amber-600"
                title={`Align F&O Position & Job families to "${fn.name}"`}>
                <AlertTriangle className="h-3 w-3" />
              </Button>
            )}
            {fn.fo_status === 'local_only' && (
              <Button size="sm" variant="ghost" onClick={() => handlePushFunction(fn)}
                className="h-7 px-1.5 opacity-0 group-hover:opacity-100 text-blue-600"
                title={`Create "${fn.name}" as an F&O Job Family`}>
                <Upload className="h-3 w-3" />
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => startRename(fn)}
              className="h-7 px-1.5 opacity-0 group-hover:opacity-100" title="Rename across Role Master">
              <Pencil className="h-3 w-3" />
            </Button>
          </>
        )}
      </div>
    );
  };

  if (!treeData) {
    return <div className="py-8 text-center text-gray-400"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading org structure...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500 flex items-start gap-1.5">
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          Live view of Role Master — Functions, Departments &amp; Teams are derived from role data.
          Renaming a node updates every matching role.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {syncMessage && <span className="text-xs text-gray-500">{syncMessage}</span>}
          <Button size="sm" variant="outline" onClick={() => loadTree(true)} disabled={isLoading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="functions">
        <TabsList>
          <TabsTrigger value="functions">
            <Building2 className="h-4 w-4 mr-1.5" />Functions
            <Badge variant="outline" className="ml-1.5 text-xs">{treeData.functions.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="departments">
            <FolderTree className="h-4 w-4 mr-1.5" />Departments &amp; Teams
            <Badge variant="outline" className="ml-1.5 text-xs">{treeData.departments.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Functions (Role Master functional_area) */}
        <TabsContent value="functions">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Building2 className="h-4 w-4" /> Functions
                  <Badge variant="outline">{treeData.functions.length}</Badge>
                </CardTitle>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                    <input type="checkbox" checked={hideEmptyFunctions} onChange={e => setHideEmptyFunctions(e.target.checked)} className="rounded border-gray-300" />
                    Active jobs only
                  </label>
                  <Button size="sm" variant="outline" onClick={() => setReconcileOpen(true)}
                    title="Compare each linked Job's family against its Role's function and fix discrepancies">
                    <GitCompareArrows className="h-3.5 w-3.5 mr-1" /> Reconcile
                  </Button>
                  <Button size="sm" variant="outline" onClick={handlePushFamilies}
                    title="Push Role functions to F&O Jobs whose family is empty">
                    <Upload className="h-3.5 w-3.5 mr-1" /> Push Families
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="divide-y">
                {treeData.functions.map(renderFunctionRow)}
              </div>

              {/* F&O Job Families that no role references */}
              {treeData.fo_only_functions && treeData.fo_only_functions.length > 0 && (
                <div className="mt-4 border-t pt-3">
                  <p className="text-xs font-medium text-gray-500 mb-2">
                    In F&O but used by no role ({treeData.fo_only_functions.length}):
                  </p>
                  <div className="space-y-1">
                    {treeData.fo_only_functions.map(fo => (
                      <div key={fo.fo_job_family_id} className="flex items-center gap-2 py-1 px-2 text-sm text-gray-500 bg-gray-50 rounded">
                        <span className="flex-1">{fo.fo_description}</span>
                        <span className="text-xs font-mono text-gray-400">{fo.fo_job_family_id}</span>
                        {fo.fo_jobs_count > 0 && <Badge variant="outline" className="text-xs">{fo.fo_jobs_count} jobs</Badge>}
                        {(fo.fo_positions_count ?? 0) > 0 && <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-200">{fo.fo_positions_count} pos</Badge>}
                        <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">F&O only</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Use Reconcile to align role functions with these F&O Job Families.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Departments & Teams (Role Master department / team_1-3) */}
        <TabsContent value="departments">
          <Card>
            <CardHeader className="py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FolderTree className="h-4 w-4" /> Departments &amp; Teams
                  <Badge variant="outline">{treeData.departments.length} depts</Badge>
                </CardTitle>
                {(treeData.unassigned_roles_count ?? 0) > 0 && (
                  <Badge variant="outline" className="text-xs text-amber-700 border-amber-200 bg-amber-50">
                    {treeData.unassigned_roles_count} role{treeData.unassigned_roles_count === 1 ? '' : 's'} with no department
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {treeData.departments.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">No departments in Role Master yet.</div>
              ) : (
                <div className="space-y-0.5">
                  {treeData.departments.map(d => renderTreeNode(d))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reassign Jobs dialog — F&O JobFamily ops key on fo_job_family_id */}
      <Dialog open={!!reassignFn} onOpenChange={(open) => { if (!open) setReassignFn(null); }} modal={false}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {reassignFn && (
            <ReassignJobsDialog
              sourceFunction={{ ...reassignFn, code: reassignFn.fo_job_family_id || reassignFn.code }}
              allFunctions={treeData.functions.filter(f => f.fo_job_family_id).map(f => ({ ...f, code: f.fo_job_family_id as string }))}
              onClose={() => { setReassignFn(null); loadTree(true); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Job ↔ Role Function Reconcile Dialog */}
      <FunctionReconcileDialog
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        onMutated={() => loadTree(true)}
      />
    </div>
  );
};

export default OrgStructureTab;
